import { v4 as uuidv4 } from 'uuid';

// Breakpoints object
export const breakpoints = {
  '640': '640px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  preview: '1100px',
  xl: '1280px',
  '2xl': '1536px',
  '1600px': '1600px',
  '3xl': '1920px',
  tall: { raw: '(min-height: 800px)' },
  'xl-max': { raw: '(max-width: 1279px)' },
  'bubble-preview-min-height': { raw: '(min-height: 1080px)' }
};

export const DANTE_ICON = 'https://dante-public-files.lon1.cdn.digitaloceanspaces.com/btn-embed-dark.png';

export const DANTE_THEME_COLOR_CHAT = {
  brand_color: '#8275F7',
  element_color: '#000000',
  surface_color: '#FFFFFF',
  alert_color: '#FF9C28',
  positive_color: '#0ED909',
  negative_color: '#FF3528'
};

// New theme color palette
export const NEW_THEME_COLOR_CHAT = {
  brand_color: '#6351FF',     // Accent
  element_color: '#0F0942',   // Dark
  surface_color: '#FFFFFF',   // Light
  alert_color: '#FF9C28',     // Keeping alert color
  positive_color: '#0ED909',  // Keeping positive color
  negative_color: '#FF3528',  // Keeping negative color
  button_color: '#EFEFF3',    // Button
  hover_color: '#D2D1E0',     // Hover
  accent_hover: '#3F29FF',    // Accent Hover
  stroke_color: '#DFDEED'     // Stroke
};

// New theme colors with separate namespace
export const NEW_THEME = {
  accent: '#6351FF',
  button: '#EFEFF3',
  hover: '#D2D1E0',
  accent_hover: '#3F29FF',
  light: '#FFFFFF',
  dark: '#0F0942',
  stroke: '#DFDEED',
  // Keep existing alert colors
  alert: '#FF9C28',
  positive: '#0ED909',
  negative: '#FF3528'
};

export const DANTE_THEME_CHAT = {
  name: 'AI Chatbot Name',
  ...DANTE_THEME_COLOR_CHAT,
  public: true,
  font_name: 'Inter',
  font_size: 15,
  input_placeholder_text: 'Write your message',
  embed_logo_url: '',
  chatbot_icon: DANTE_ICON,
  hidden_powered_by_dante: false,
  initial_messages: [{
    role: 'assistant',
    type: 'welcome_message',
    content: '👋 Hi there how can I help you?'
  }],
  custom_css: ''
};

export const STATUS = {
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  WORKING: 'WORKING',
  ERROR: 'ERROR',
  AI: 'AI',
  TAKEN: 'TAKEN',
  CLOSED: 'CLOSED',
  DROPPED: 'DROPPED',
  RESOLVED: 'RESOLVED',
  UNVERIFIED: 'UNVERIFIED',
  VERIFIED: 'VERIFIED'
};

export const TASK_TYPES = {
  SUGGESTION: 'Suggestion',
  TRAINING: 'Training'
};

export const iconToCompanyName = {
  OpenAiLogoIcon: 'Open AI',
  ClaudeLogoIcon: 'Anthropic',
  CohereIcon: 'Cohere',
  GoogleLogoIcon: 'Google'
};

export const NOTIFICATION_MESSAGES = {
  FAILED_IN_APP: 'We could not process your request. Please try again and if the issue persists, contact us on support@dante-ai.com for help.',
  FAILED_IN_EMBED: 'We could not process your request. Please try again and if the issue persists, contact support for help.',
  FAILED_LARGE_FILE: 'The files you are trying to upload are larger than maximum limit of 128Mb. Please make sure to be below this limit.',
  USER_NOT_VERIFIED: 'Your account is not verified. Please verify your account and then try again',
  LOGIN_BAD_CREDENTIALS: 'Wrong email or password provided. Please double check your credentials',
  VERIFY_USER_ALREADY_VERIFIED: 'This account is already verified and you can login',
  REGISTER_USER_ALREADY_EXISTS: 'This email is already registered',
  LOGIN_BAD_OTP: 'Invalid OTP code',
  LOGIN_BAD_EMAIL: 'Invalid email',
  LOGIN_BAD_PASSWORD: 'Invalid password',
  RESET_PASSWORD_BAD_TOKEN: 'This link has expired. Please request a new password reset link.',
  GOOGLE_USER_NOT_FOUND: 'This Google account is not registered. Please sign up first to create an account.',
  SERVER_ERROR: 'Something went wrong. Please try again later or contact support@dante-ai.com'
};

export const AGENT_MEMBER_PERMISSIONS = [
  {
    name: 'View only',
    description: 'Can view chatbots and access analytics.',
    value: 'view_only'
  },
  {
    name: 'Chat agent',
    description: 'Can chat with chatbots.',
    value: 'chat_agent'
  }
];

export const COMMON_CLASSNAMES = {
  transition: {
    duration: {
      short: 'duration-150',
      medium: 'duration-300',
      long: 'duration-500'
    },
    delay: {
      short: 'delay-75',
      medium: 'delay-150',
      long: 'delay-300'
    }
  },
  previewBubble: 'w-[368px] lg:w-[368px] 2xl:w-[440px] h-full bubble-preview-min-height:h-[792px] relative flex flex-col gap-size2 rounded-size1 flex-shrink-0 grow pb-3'

}

export const VOICE_DASHBOARD_DEFAULT_VOICES = [
  {
    id: '1',
    name: 'Voice 1',
    chatbotName: 'Chatbot 1',
    phoneNumbers: [{ number: '+33600000000' }, { number: '+33600000001' }]
  },
  {
    id: '2',
    name: 'Voice 2',
    chatbotName: 'Chatbot 2',
    phoneNumbers: [{ number: '+33600000002' }, { number: '+33600000003' }]
  }
]

export const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const TIME_OPTIONS = Array.from({ length: 96 }, (_, index) => ({
  value: `${(Math.floor(index / 2) % 24).toString().padStart(2, '0')}:${(index % 2 === 0 ? '00' : '30')}`,
  label: `${(Math.floor(index / 2) % 24).toString().padStart(2, '0')}:${(index % 2 === 0 ? '00' : '30')}`
}));

export const LLM_MODEL_DEFAULT = {
  Logo: 'OpenAiLogoIcon',
  value: 'gpt-4-omnimodel-mini',
  label: 'GPT-4o Mini',
  description:
    'Advanced cost-efficient intelligence. 0.25 credits per response.',
  credits: 0.25
};

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/svg+xml'];

export const MODULE_NAMES = {
  chatbot: {
    name: 'AI Chatbot',
    singular: 'AI Chatbot',
    plural: 'AI Chatbots'
  },
  personas: {
    name: 'AI Digital Persona',
    singular: 'AI Digital Persona',
    plural: 'AIDigital Personas'
  },
  voice: {
    name: 'AI Voice',
    singular: 'AI Voice',
    plural: 'AI Voices'
  },
  handover: {
    name: 'Human Handover',
    singular: 'Human Handover',
    plural: 'Human Handovers'
  },
  insights: {
    name: 'Insights',
    singular: 'Insight',
    plural: 'Insights'
  }
};

export const fakerSlots = {
  // Buttons structure
  buttons: [
    {
      label: '🗓️ Book a Demo',
      icon: 'calendar',
      link: 'https://calendly.com/jetelira/dante-ai-demo',
      type: 'primary',
      order: 0
    },
    {
      label: '📗 Guides',
      icon: 'book',
      link: 'https://www.dante-ai.com/guides',
      type: 'secondary',
      order: 1
    }
  ],

  // Titles structure
  titles: [
    {
      content: 'Welcome to Dante AI',
      order: 0
    },
    {
      content: 'Explore Our Features',
      order: 1
    }
  ],

  // Cards structure (new format)
  cards: [
    {
      card_type: 'image',
      disclaimer: 'Image card',
      order: 1, // Position after "Book a Demo" (order: 0)
      card: {
        card_slot_id: uuidv4(),
        image_url: 'https://dante-public-files.lon1.cdn.digitaloceanspaces.com/Jeta.png',
        image_title: "A marathon of sprints: one leader's philosophy for building an AI startup",
        image_description: 'This pivotal moment underscores the essence of our mission at Dante AI. We\'re not just part of this transformation; we\'re leading it by enabling businesses to navigate the shift from traditional systems to AI-driven innovation.',
        order: 0,
      }
    }
  ],

  // Image cards structure
  // image_cards: [
  //   {
  //     image_url: 'https://dante-public-files.lon1.cdn.digitaloceanspaces.com/Image1.png',
  //     image_title: 'AI Chatbot Solutions',
  //     image_description: 'Discover how our AI chatbots can transform your customer service experience.',
  //     order: 2 // After image cards from the new cards structure
  //   }
  // ],

  // Meta link cards structure
  // meta_link_cards: [
  //   {
  //     url: 'https://www.raconteur.net/leadership/one-tech-leaders-philosophy-for-building-an-ai-startup',
  //     meta_title: "A marathon of sprints: one leader's philosophy for building an AI startup",
  //     meta_description: 'This pivotal moment underscores the essence of our mission at Dante AI. We\'re not just part of this transformation; we\'re leading it by enabling businesses to navigate the shift from traditional systems to AI-driven innovation.',
  //     order: 3 // After image cards
  //   }
  // ],

  // Video cards structure
  // video_cards: [
  //   {
  //     video_url: 'https://www.youtube.com/watch?v=example',
  //     video_title: 'Getting Started with Dante AI',
  //     video_description: 'Learn how to set up and configure your first AI chatbot in just a few minutes.',
  //     order: 4 // After meta link cards
  //   }
  // ],

  // Legacy structure for backward compatibility
  quick_links: [
    {
      id: uuidv4(),
      user_id: 'e3a81a8f-7eab-415c-b9d8-1477103e7d2b',
      kb_id: 'ad8d93de-50cb-442f-b1a6-abe9606511d3',
      title: '🗓️ Book a Demo',
      url: 'https://calendly.com/jetelira/dante-ai-demo',
      open_in_new_tab: true,
      pull_meta_data: false,
      order: 0,
      disclaimer: 'Quick link',
      type: 'quick_link'
    },
  ],
  meta_links: [
    {
      id: uuidv4(),
      user_id: 'e3a81a8f-7eab-415c-b9d8-1477103e7d2b',
      kb_id: 'ad8d93de-50cb-442f-b1a6-abe9606511d3',
      title: "A marathon of sprints: one leader's philosophy for building an AI startup",
      url: 'https://www.raconteur.net/leadership/one-tech-leaders-philosophy-for-building-an-ai-startup',
      open_in_new_tab: true,
      pull_meta_data: true,
      order: 3, // Consistent with meta_link_cards order
      disclaimer: 'Meta links disclaimer',
      type: 'meta_link',
      description: 'This pivotal moment underscores the essence of our mission at Dante AI. We\'re not just part of this transformation; we\'re leading it by enabling businesses to navigate the shift from traditional systems to AI-driven innovation.',
      image_url: 'https://dante-public-files.lon1.cdn.digitaloceanspaces.com/Jeta.png'
    }
  ],
  link_groups: [
    {
      kb_id: 'ad8d93de-50cb-442f-b1a6-abe9606511d3',
      title: '📗 Guides',
      items: [
        {
          id: 1,
          name: "How to access Dante AI's API?",
          url: 'https://www.dante-ai.com/guides/how-to-access-dante-ais-api',
          order: 0
        },
        {
          id: 2,
          name: 'How to integrate with WhatsApp?',
          url: 'https://www.dante-ai.com/guides/whatsapp-integration',
          order: 1
        }
      ],
      open_all_in_new_tab: true,
      order: 5,
      type: 'link_group',
      disclaimer: 'Link group'
    }
  ],
  // Sliders structure (updated format) - Single slider with multiple slides
  sliders: [
    {
      title: '🗞️ AI News and Views',
      type: 'landscape',
      order: 10, // High order number to show at the end
      items: [
        {
          slide_title: 'Chatbot vs Conversational AI Chatbots: What\'s the Difference?',
          slide_description: 'This article dives into the differences between traditional chatbots and AI-powered chatbots, examining how they function, their unique features, and when each type is best suited for a business.',
          slide_url: 'https://www.dante-ai.com/article/chatbot-vs-conversational-ai-chatbots-whats-the-difference',
          slide_image_url: 'https://dante-public-files.lon1.cdn.digitaloceanspaces.com/Image1.png',
          order: 0
        },
        {
          slide_title: 'What are the Risks of AI in Customer Service?',
          slide_description: 'In this article, we\'ll explore some important aspects of AI adoption, such as data privacy, technical reliability, and maintaining the human touch in service interactions.',
          slide_url: 'https://www.dante-ai.com/article/what-are-the-risks-of-ai-in-customer-service',
          slide_image_url: 'https://dante-public-files.lon1.cdn.digitaloceanspaces.com/Image2.png',
          order: 1
        },
        {
          slide_title: 'What Companies Use AI for Customer Service?',
          slide_description: 'This article explores how companies across industries are transforming customer service with artificial intelligence (AI). Discover how AI-driven tools like chatbots and voice recognition technology streamline routine tasks, allowing human agents to focus on complex issues.',
          slide_url: 'https://www.dante-ai.com/article/what-companies-use-ai-for-customer-service',
          slide_image_url: 'https://dante-public-files.lon1.cdn.digitaloceanspaces.com/Image3.png',
          order: 2
        }
      ]
    }
  ],

  // Legacy sliders structure for backward compatibility
  legacy_sliders: [
    {
      kb_id: '',
      title: '🗞️ AI News and Views',
      items: [
        {
          id: uuidv4(),
          url: 'https://www.dante-ai.com/article/chatbot-vs-conversational-ai-chatbots-whats-the-difference',
          label: '',
          name: 'Chatbot vs Conversational AI Chatbots: What\'s the Difference?',
          open_in_new_tab: true,
          description: 'This article dives into the differences between traditional chatbots and AI-powered chatbots, examining how they function, their unique features, and when each type is best suited for a business. ',
          thumbnail_url: 'https://dante-public-files.lon1.cdn.digitaloceanspaces.com/Image1.png',
          order: 0
        },
        {
          id: uuidv4(),
          url: 'https://www.dante-ai.com/article/what-are-the-risks-of-ai-in-customer-service',
          label: '',
          name: 'What are the Risks of AI in Customer Service?',
          open_in_new_tab: true,
          description: "In this article, we'll explore some important aspects of AI adoption, such as data privacy, technical reliability, and maintaining the human touch in service interactions.",
          thumbnail_url: 'https://dante-public-files.lon1.cdn.digitaloceanspaces.com/Image2.png',
          order: 1
        },
        {
          id: uuidv4(),
          url: 'https://www.dante-ai.com/article/what-companies-use-ai-for-customer-service',
          label: '',
          name: 'What Companies Use AI for Customer Service?',
          open_in_new_tab: true,
          description: 'This article explores how companies across industries are transforming customer service with artificial intelligence (AI). Discover how AI-driven tools like chatbots and voice recognition technology streamline routine tasks, allowing human agents to focus on complex issues. ',
          thumbnail_url: 'https://dante-public-files.lon1.cdn.digitaloceanspaces.com/Image3.png',
          order: 2
        }
      ],
      open_all_in_new_tab: true,
      order: 10, // High order number to show at the end
      type: 'slider',
      disclaimer: 'Slider'
    }
  ],
  // Legacy video structure for backward compatibility
  videos: []
};

export const SYSTEM_FONTS = [
  'Inter',
  'Arial',
  'Helvetica',
  'Georgia',
  'Times New Roman',
  'Tahoma',
  'Trebuchet MS',
  'Verdana',
  'Courier New',
  'Impact',
  'Lucida Sans Unicode',
  'Geneva',
  'Lucida Console',
];

export const EMAILS = {
  SALES: 'jeta@dante-ai.com'
}

export const DEFAULT_CHAT_MESSAGES = {
  CONSENT_MESSAGE: 'By continuing, you agree to share your information with us for the purpose of providing you with a personalized experience.'
}

export const TRIAL_DAYS = 14;

export const LIVE_AGENT_AVAILABILITY = {
  ALWAYS: 'always',
  OFFICE_HOURS: 'office_hours',
  CUSTOM: 'custom'
}

export const HUMAN_HANDOVER_CHAT_BOX_LIMIT = 3;

export const TASK_POLLING_INTERVAL = 3000;
export const JOB_POLLING_INTERVAL = 3000;
export const GENERAL_POLLING_INTERVAL = 6000;

export const MAX_URLS_PER_ENTIRE_DOMAIN = -1;

export const NOTIFICATION_SOUND_URL = 'https://dante-public-files.lon1.cdn.digitaloceanspaces.com/notification_sound.mp3';

export const CHATBOTS_WITH_NEW_DESIGN = [
  // '996c0c59-c322-4194-a016-a85f45c365ed',
  // '86927718-7690-4c0c-a99d-8bc8afda0a4c',
  // '769b635b-46a6-41e4-8477-238dcd32ef2f'
]

export const SOUND_EFFECTS = {
  // Sound effect types
  TOOLTIP_SHOW: 'tooltip_show',
  MESSAGE_SEND: 'message_send',
  LIVE_AGENT_MESSAGE: 'live_agent_message',

  // Sound configuration
  CONFIG: {
    volume: 0.3, // Default volume (0.0 to 1.0)
    enabled: true, // Global sound toggle
    tooltipEnabled: true,
    messageSendEnabled: true,
    liveAgentMessageEnabled: true
  },

  // Sound options for bubble messages
  BUBBLE_SOUND_OPTIONS: [
    { id: 'sound1', name: 'Sound 1', file: 'https://dante-public-files.lon1.cdn.digitaloceanspaces.com/new-notification-07-210334.mp3' },
    { id: 'sound2', name: 'Sound 2', file: 'https://dante-public-files.lon1.cdn.digitaloceanspaces.com/new-notification-09-352705.mp3' },
    { id: 'sound3', name: 'Sound 3', file: 'https://dante-public-files.lon1.cdn.digitaloceanspaces.com/new-notification-018-363746.mp3' },
    { id: 'sound4', name: 'Sound 4', file: 'https://dante-public-files.lon1.cdn.digitaloceanspaces.com/new-notification-08-352461.mp3' }
  ]
};

// Sound effect enum for easy switching
export const SOUND_EFFECT_ENUM = {
  TOOLTIP_SHOW: 'TOOLTIP_SHOW',
  MESSAGE_SEND: 'MESSAGE_SEND',
  LIVE_AGENT_MESSAGE: 'LIVE_AGENT_MESSAGE'
};

// Resolution feedback question messages
export const RESOLUTION_FEEDBACK_QUESTIONS = [
  'Did this help?',
  'Let me know, did that help?'
];

// Resolution feedback response messages
export const RESOLUTION_RESPONSE_MESSAGES = [
  'Great, happy that solved it for you.',
  'Nice, glad that worked out.',
  'Perfect, happy we could clear that up.',
  'Awesome, good to know that helped.',
  'Sweet, glad that was useful.',
  'Happy to hear that worked for you.',
  'Nice, thanks for letting me know it helped.',
  'Great, always happy to make things easier.'
];

// Resolution negative feedback messages
export const RESOLUTION_NEGATIVE_MESSAGES = [
  'Sorry, that did not hit the mark. Let me try again.',
  'My bad, let me take another shot at this.',
  'Sorry about that. Let me try again.',
  'Thanks for the feedback. Let me try again.',
  'Sorry, that was not helpful enough. Let me try again.'
];

// CSAT thank you messages
export const RESOLUTION_THANK_YOU_MESSAGES = [
  'Great, happy to help. Have a smooth day.',
  'Nice, glad everything is sorted.',
  'Sweet, reach out anytime if you need more help.',
  'Perfect, I am here if anything else comes up.',
  'Good stuff, feel free to return whenever you want.',
  'Great, take care and let me know if anything changes.',
  'Thanks for stopping by, I am always here to help.'
];

// Acknowledgment messages
export const ACKNOWLEDGMENT_MESSAGES = [
  'Sure thing. Tell me what you need next.',
  'No problem. What else can I help you with?',
  'Happy to help more. What is your next question?',
  'Sure. What would you like to check next?',
  'Let\'s keep going. What do you need?',
  'Of course. What can I help you with now?',
  'Ask away. I\'ve got you.'
];

